import CustomScrollbar from 'components/Common/CustomScrollbar';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import PriorityUpdatesList from 'components/Dashboard/PriorityUpdates/PriorityUpdatesList';
import DateReplacements from 'components/Dashboard/UpcomingEvents/DatesReplacements';
import calculateDifferenceBetweenDays from 'components/Dashboard/utilities/calculateDifferenceBetweenDays';
import currentDate from 'components/Dashboard/utilities/currentDateUTC';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import { store } from 'components/Store';
import dayjs from 'dayjs';
import React, { useState, useRef, useEffect, useContext } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { Link } from 'react-router';
import MoreFiltersButton from 'components/Dashboard/Filters/ui/MoreFiltersButton';
import preventLink from 'components/Dashboard/TagsList/prevenLink';
import LeftComponentTopicPicker from 'components/Dashboard/DashboardComponent/LeftComponentTopicPicker';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import time from 'utils/time';

const LeftComponentTitle = ({ title, setActiveTab, activeTab }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectElement = useRef(null);
  useOutsideElement(selectElement, () => setIsOpen(false));
  const showPriorityUpdates = import.meta.env.VITE_APP_SHOW_PRIORITY_UPDATES === 'true';

  return (
    <div ref={selectElement} className='position-relative'>
      <h3
        className={`main-title mb-0 d-flex align-items-center`}
        role={showPriorityUpdates ? 'button' : undefined}
        tabIndex={showPriorityUpdates ? 0 : undefined}
        onClick={() => showPriorityUpdates && setIsOpen(!isOpen)}
      >
        {title}
      </h3>
      {showPriorityUpdates && isOpen && (
        <div
          className={`box-shadow py-2 position-absolute z-extra-max-plus rounded d-flex flex-column ${
            activeTab === 'priorityUpdates' ? '' : 'flex-column-reverse'
          }`}
          style={{ right: '0px', top: 'calc(100% + 5px)', width: '230px' }}
        >
          <button
            className='w-100 list-action-button py-2 general-button text-left nowrap-item specific-right-padding-24 flex-centered flex-nowrap justify-content-between px-3'
            onClick={() => {
              setActiveTab('priorityUpdates');
              setIsOpen(false);
            }}
          >
            <span>Today's priority updates</span>
            {activeTab === 'priorityUpdates' && <span className='icon-tick' />}
          </button>
          <button
            className='w-100 list-action-button py-2 general-button text-left nowrap-item  specific-right-padding-24 flex-centered flex-nowrap justify-content-between px-3'
            onClick={() => {
              setActiveTab('upcomingEvents');
              setIsOpen(false);
            }}
          >
            <span>Key upcoming events</span>
            {activeTab === 'upcomingEvents' && <span className='icon-tick' />}
          </button>
        </div>
      )}
    </div>
  );
};
const LeftComponent = (props) => {
  const [activeTab, setActiveTab] = useState('upcomingEvents');
  const showPriorityUpdates = import.meta.env.VITE_APP_SHOW_PRIORITY_UPDATES === 'true';

  return (
    <div
      className={`h-100 d-none d-lg-block user-select-none px-0 position-relative ${
        showPriorityUpdates && activeTab === 'priorityUpdates'
          ? 'dashboard-panel-priority-updates'
          : 'dashboard-panel-upcoming-events'
      }`}
    >
      <div className='box-top-rounded h-100 bg-white dashboard-root-container'>
        {showPriorityUpdates && activeTab === 'priorityUpdates' ? (
          <LeftComponentPriorityUpdates {...props} setActiveTab={setActiveTab} activeTab={activeTab} />
        ) : (
          <LeftComponentKeyUpdates {...props} setActiveTab={setActiveTab} activeTab={activeTab} />
        )}
      </div>
    </div>
  );
};

const LeftComponentKeyUpdates = (props) => {
  const { date, setActiveTab, activeTab } = props;
  const globalState = useContext(store);
  const { state } = globalState;
  const { team } = state;
  const { newAgencyPlan } = useTopicOrClientWord();
  const [isLoading, setIsLoading] = useState(true);
  const { generalApiCall } = useGeneralApiCall();
  const [results, setResults] = useState({});

  useEffect(() => {
    const source = new AbortController();
    initialCall.current(source);
    return () => {
      source.abort('Left component cancelled by the user');
    };
  }, [team?.teamId, date]);

  const initialCall = useRef(null);
  initialCall.current = async (source) => {
    try {
      setIsLoading(true);
      const consultations = await contentApiCall({ type: 'Consultation', source });
      const events = await contentApiCall({
        type: 'Event',
        source,
        pageSize: 10000,
        field: 'eventStartDate',
      });
      if (consultations && events) {
        // Combine hits from both
        const allItems = [
          ...(consultations.hits || []).map((item) => ({
            ...item,
            groupDate: time.getCalendarDateKey(item.closeDate),
          })),
          ...(events.hits || []).map((item) => ({
            ...item,
            groupDate: time.getCalendarDateKey(item.eventStartDate),
          })),
        ];

        const groupedByDate = allItems.reduce((acc, item) => {
          const date = item.groupDate;
          if (!acc[date]) acc[date] = [];
          acc[date].push(item);
          return acc;
        }, {});

        // You can now use groupedByDate as needed
        // Example: setState(groupedByDate);
        setResults(groupedByDate);
        setIsLoading(false);
      }
    } catch (error) {}
  };

  const contentApiCall = async ({
    source,
    pathname = `/api/search/query-by-keyword-list-v2`,
    pageSize = 20,
    type,
    field = 'closeDate',
  }) => {
    try {
      const requestProperties = {
        filters: [
          { field, value: date ?? currentDate, operator: 'DATE_GTE' },
          {
            field,
            value: dayjs(date ?? undefined)
              .startOf('day')
              .add('1', 'month')
              ?.format('YYYY-MM-DD'),
            operator: 'DATE_LT',
          },
        ],
        includeExplanations: false,
        keywordListId: null,
        pageNumber: 1,
        pageSize,
        type,
      };
      return await generalApiCall({
        pathname,
        method: 'post',
        needsAuthentication: true,
        notShowErrorMessage: true,
        requestProperties,
        requestSource: source,
      });
    } catch (error) {}
  };
  const resultsKeys = Object.keys(results).sort((a, b) => dayjs(a).diff(dayjs(b)));

  return (
    <>
      <div
        className='flex-centered border-bottom specific-x-padding-22 specific-x-padding-lg-30 py-3 justify-content-between'
        style={{ minHeight: '90px' }}
      >
        <LeftComponentTitle title='Key upcoming events' setActiveTab={setActiveTab} activeTab={activeTab} />
        <Link
          to='/calendar'
          className={`general-button border rounded-pill d-none d-lg-flex align-items-center justify-content-center action-state-hover light-90-text nowrap-item reset-link circle-button circle-button-38`}
        >
          <span className='icon-calendar' data-tooltip-id='general-tooltip' data-tooltip-content='See full calendar' />
        </Link>
      </div>
      <div className='flex-grow-1'>
        <CustomScrollbar className={`simple-scrollbar`} maximalThumbYSize={100}>
          {isLoading ? (
            <SkeletonTheme baseColor='#f2f2f2'>
              {Array(4)
                .fill()
                .map((item, index) => (
                  <div key={index} className={`border-bottom specific-x-padding-22 specific-x-padding-lg-30 py-3 `}>
                    <h3 className='title-h5-m-bold mb-3'>
                      <Skeleton height={19} width={`45%`} />
                    </h3>

                    <Skeleton count={4} height={19} width={`100%`} />
                  </div>
                ))}
            </SkeletonTheme>
          ) : (
            <>
              {resultsKeys?.length > 0 ? (
                <>
                  {resultsKeys?.map((item) => {
                    return <LeftComponentItem key={`item-left-${item}`} date={item} items={results[item]} />;
                  })}
                </>
              ) : (
                <div className='h-100 flex-centered justify-content-center'>
                  <p className='mb-0 light-60-text title-h5-m px-4 text-center' style={{ marginTop: '-100px' }}>
                    No upcoming events found{' '}
                    {newAgencyPlan ? (
                      <>
                        <br />
                        for {team?.teamName}
                      </>
                    ) : (
                      ''
                    )}
                  </p>
                </div>
              )}
            </>
          )}
        </CustomScrollbar>
      </div>
    </>
  );
};

const LeftComponentItem = (props) => {
  const { date, items } = props;
  const replacement = DateReplacements(date);
  return (
    <div
      className={`border-bottom specific-x-padding-22 specific-x-padding-lg-30 py-4 ${
        replacement === 'Today' ? 'bg-blue-hovered' : ''
      }`}
    >
      <h3 className='title-h5-m-bold mb-3'>
        {replacement}
        {!!replacement ? ', ' : ''}
        {time(time.getCalendarDateKey(date)).formatDayFullMonth()}
      </h3>
      {items?.map((item) => {
        return (
          <React.Fragment key={`item-${item?.objectID}`}>
            {item?.type === 'Consultation' && <LeftConsultationItem item={item} />}
            {item?.type === 'Event' && <LeftEventItem item={item} />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const LeftConsultationItem = (props) => {
  const { item } = props;
  const { departmentName, title, closeDate, contentUrl } = item;

  const closeText = () => {
    const today = time().startOf('day');
    const close = time(time.getCalendarDateKey(closeDate)).startOf('day');
    const diff = close.diff(today, 'day');

    let closesText;
    if (diff === 0) {
      closesText = 'today';
    } else if (diff === 1) {
      closesText = 'in 1 day';
    } else if (diff > 1) {
      closesText = `in ${diff} days`;
    } else {
      closesText = `Closed`;
    }
    return closesText;
  };

  const closesTextElement = useRef(null);
  const containerElement = useRef(null);
  const [startsNewLine, setStartsNewLine] = useState(false);

  useEffect(() => {
    const checkLineBreak = () => {
      const paragraph = containerElement.current;
      const lastSpan = closesTextElement.current;

      if (!paragraph || !lastSpan) return;

      const spanRects = lastSpan.getClientRects();
      const paraRects = paragraph.getClientRects();

      if (spanRects.length === 0 || paraRects.length === 0) return;

      const spanFirstRect = spanRects[0];
      const paraFirstRect = paraRects[0];

      const onNewLine = spanFirstRect.top > paraFirstRect.top;

      const startsLine = Math.round(lastSpan.offsetLeft) === Math.round(paragraph.offsetLeft);

      const isFirstOnSecondLine = onNewLine && startsLine;

      setStartsNewLine((prev) => (prev !== isFirstOnSecondLine ? isFirstOnSecondLine : prev));
    };

    // Debounce resize events
    let timeout;
    const handleResize = () => {
      clearTimeout(timeout);
      timeout = window.setTimeout(() => {
        requestAnimationFrame(checkLineBreak);
      }, 100);
    };

    checkLineBreak();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className='flex-centered align-items-start pt-1 mb-2 flex-nowrap'>
      <div className='icon-container-consultation pr-3 specific-top-padding-5'>
        <span className='icon-consultation title-h4' />
      </div>
      <div>
        <a
          target={'_blank'}
          rel='noopener noreferrer'
          href={contentUrl}
          className='reset-link general-link text-main-blue d-block'
        >
          <span className='font-weight-bold'>Closing consultation</span>: {title}
        </a>
        <p
          className={`mb-0 light-50-text mt-1 ${startsNewLine ? 'dashboard-consultation-starting-line' : ''}`}
          ref={containerElement}
        >
          {departmentName}{' '}
          <span ref={closesTextElement}>
            <span className='dot d-inline-block-centered centered-2' />{' '}
            <span className={`${calculateDifferenceBetweenDays(closeDate) > 7 ? '' : 'below-seven'}`}>
              Closes {closeText()}
            </span>
          </span>
        </p>
      </div>
    </div>
  );
};

const LeftEventItem = (props) => {
  const { item } = props;
  const { title, eventCategoryName, id, eventStartDate, dateTime } = item;
  const calendarDate = time.getCalendarDateKey(eventStartDate ?? dateTime);
  return (
    <div className='flex-centered align-items-start pt-1 mb-2 flex-nowrap'>
      <div className='icon-container-consultation pr-3 specific-top-padding-5'>
        <span className='icon-calendar title-h4' />
      </div>
      <div className='flex-grow-1'>
        <a
          target={'_blank'}
          rel='noopener noreferrer'
          href={`/calendar/${calendarDate}?eventId=${id}`}
          className='reset-link general-link text-main-blue d-block'
        >
          {title}
        </a>
        <p
          className={`mb-0 ${
            eventCategoryName?.toLowerCase()?.includes('commons')
              ? 'commons-color'
              : `${eventCategoryName?.toLowerCase()?.includes('lords') ? 'lords-color' : 'light-60-text'}`
          }`}
        >
          {eventCategoryName}
        </p>
      </div>
    </div>
  );
};

const LeftComponentPriorityUpdates = (props) => {
  const { updateState, resetEventParameter, setActiveTab, activeTab } = props;
  const { setUpdatePanelState, updatePanelState } = updateState ?? {};
  const isMobile = useIsMobile();
  const onClick = (e) => {
    const anchor = e?.target?.closest('a');
    const href = anchor ? anchor.pathname + anchor.search + anchor.hash : '';

    if (href && href.startsWith('/') && !href.startsWith('/monitor-inbox/upcoming-events')) {
      if (!isMobile && setUpdatePanelState && resetEventParameter) {
        preventLink(e);
        const currentCount = updatePanelState?.timesClicked?.[href] ?? 0;
        const newNumber = currentCount + 1;
        resetEventParameter();
        setUpdatePanelState({
          open: true,
          url: href,
          timesClicked: { ...(updatePanelState?.timesClicked ?? {}), [href]: newNumber },
          highlightUpdate: newNumber > 1,
        });
      }
    }
  };
  return (
    <>
      <div
        className='flex-centered border-bottom specific-x-padding-22 specific-x-padding-lg-30 py-3 justify-content-between'
        style={{ minHeight: '90px' }}
      >
        <LeftComponentTitle title="Today's priority updates" setActiveTab={setActiveTab} activeTab={activeTab} />
        <MoreFiltersButton section={'Dashboard'} circleButton />
      </div>
      <LeftComponentTopicPicker />
      <div className='flex-grow-1 specific-top-padding-2'>
        <div className='specific-x-padding-22 h-100'>
          <PriorityUpdatesList widget={true} onClick={onClick} />
        </div>
      </div>
    </>
  );
};

export default LeftComponent;
